import Select from "react-select";
import SelectStyles from "../../common/selectStyles";
import {
  SelectOption,
} from "../../common/types";
import { AppLabels } from "../../common/localization";
import Card from "./Card";
import { useMemo, useState } from "react";
import { AvailableLocation } from "../../common/hooks";
import { ReactComponent as ExternalIcon } from '../../assets/svg/external.svg';
import { Alert } from "react-bootstrap";

type PickupLocationSelectorProps = {
  lang: string,
  provinces: SelectOption[],
  locations: AvailableLocation[],
  selectedProvince: string,
  selectedLocationID: number,
  handleProvinceChange: (province: string) => void,
  handleLocationChange: (locationId: number) => void,
  validated?: boolean,
}

const PickupLocationSelector = ({lang, provinces, locations, selectedProvince, selectedLocationID, handleProvinceChange, handleLocationChange, validated }: PickupLocationSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const isProvinceSelected = useMemo(() => selectedProvince && selectedProvince.length > 0 && locations.some((x) => x.value === selectedProvince), [locations, selectedProvince]);
  const availableLocations = useMemo(() => {
    if(selectedLocationID > 0) {
      return locations.flatMap(x => x.options).filter(x => x.id === selectedLocationID);
    }

    const ops = isProvinceSelected
      ? (locations.find(
          (x) => x.value === selectedProvince,
        )?.options ?? [])
      : locations.flatMap((x) => x.options)

      return ops.filter(x => {
        const name = x.names[lang] || x.names['en'] || '';
        const address = x.addresses[lang] || x.addresses['en'] || '';

        return name.toLowerCase().includes(searchTerm.toLowerCase())
        || address.toLowerCase().includes(searchTerm.toLowerCase())
      });
    },
    [isProvinceSelected, locations, selectedProvince, lang, searchTerm, selectedLocationID]);

  const onLocationSelected = (locationId: number) => {
    if(selectedLocationID === locationId) {
      handleLocationChange(0);
    } else {
      handleLocationChange(locationId);
    }
  }

  return (
    <Card>
      <div>
        <span className="font-weight-bold">{AppLabels.Location}</span>
      </div>
      <div className="row">
        <div className="col-12 col-sm-6">
          <div className="mt-3">
            <label className="mb-1" htmlFor="pickupProvince" id="pickupProvinceLabel">
              {AppLabels.Province}
            </label>
            <span className="visually-hidden" id="pickupProvinceHelp">
              {AppLabels.ProvinceFilterHelp}
            </span>
            <Select
              inputId="pickupProvince"
              aria-labelledby="pickupProvinceLabel pickupProvinceHelp"
              styles={SelectStyles}
              options={provinces}
              name="pickupProvince"
              value={provinces.find(
                (x) => x.value === selectedProvince,
              )}
              isSearchable={false}
              placeholder={AppLabels.Select}
              isDisabled={selectedLocationID > 0}
              onChange={(value: any) => {
                handleProvinceChange(value.value)
              }}
            ></Select>
          </div>
        </div>
        <div className="col-12 col-sm-6">
          <div className="mt-3">
            <label className="mb-1" htmlFor="locationSearch">
              {AppLabels.PickupLocation}
            </label>
            <span className="visually-hidden" id="locationSearchHelp">
              {AppLabels.LocationSearchHelp}
            </span>
            <input
              type="text"
              value={searchTerm}
              placeholder={AppLabels.SearchLocation}
              id="locationSearch"
              className="form-control mb-2"
              aria-describedby="locationSearchHelp"
              aria-invalid={validated && selectedLocationID <= 0}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={selectedLocationID > 0}
            />
            <div
              role="alert"
              className={`text-danger ${
                validated && selectedLocationID <= 0 ? "" : "d-none"
              }`}
            >
              {AppLabels.LocationValidation}
            </div>
          </div>
        </div>
      </div>
      <span className="visually-hidden" id="locationListInstructions">
        {AppLabels.LocationListInstructions}
      </span>
      <div className="visually-hidden" aria-live="polite">
        {AppLabels.formatString(AppLabels.LocationsShown, availableLocations.length)}
      </div>
      <div className="mt-3 row" role="group" aria-label={AppLabels.PickupLocation} aria-describedby="locationListInstructions">
        {availableLocations.map(loc => {
          const name = loc.names[lang] || loc.names['en'] || '';
          const address = loc.addresses[lang] || loc.addresses['en'] || '';
          return (
            <div className="col-12 col-sm-6 d-flex" key={loc.id}>
              <div className={`pickup-location p-2 border rounded mb-2 w-100 ${selectedLocationID === loc.id ? 'border-primary bg-primary text-white' : 'border-secondary'}`}
                role="button"
                tabIndex={0}
                aria-pressed={selectedLocationID === loc.id}
                aria-label={address !== '' ? `${name}, ${address}` : name}
                onClick={() => onLocationSelected(loc.id)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onLocationSelected(loc.id);
                  }
                }}
                style={{ cursor: 'pointer' }}>
                <div className="font-weight-bold">{name}</div>
                {address !== '' && (<>
                  <div>{address}</div>
                  <div className="text-end">
                    <a href={`https://www.google.com/maps/search/?api=1&query=${address}`} onClick={e => e.stopPropagation()}
                      target="_blank" rel="noopener noreferrer"
                      aria-label={`${AppLabels.ViewMap} - ${name}`}
                      className={`py-1 d-inline-flex ${selectedLocationID === loc.id ? 'color-inherit' : ''}`}>
                      {AppLabels.ViewMap}
                      <ExternalIcon className="ml-1" width={16} height={16} aria-hidden="true" />
                    </a>
                  </div>
                </>)}
              </div>
            </div>
          )}
        )}
        {availableLocations.length === 0 && <div className="col-12">
          <Alert variant="info">
            {AppLabels.NoLocations}
          </Alert>
          </div>}
      </div>
    </Card>
  );
}

export default PickupLocationSelector